import ObjectUtil from "../../../../util/ObjectUtil"
import RemoteEvaluateClient from "../../client/RemoteEvaluateClient"
import { WorkspaceEvaluateResponseDto } from "../../model/dto"
import { WorkspaceEvaluationContext } from "../../WorkspaceEvaluationContext"
import { WorkspaceEvaluations } from "../WorkspaceEvaluations"
import WorkspaceRemoteEvaluator from "../WorkspaceRemoteEvaluator"
import { FullWorkspaceEvaluateRequest } from "./FullWorkspaceEvaluateRequest"
import { FullWorkspaceEvaluateResponse } from "./FullWorkspaceEvaluateResponse"

/**
 * Evaluates the whole workspace. A null response (HTTP 204) keeps the cached base; a FULL response
 * replaces it; a DELTA response is merged into the base and the merged hash is re-checked — a
 * mismatch forces a FULL refetch so the client never diverges from the server.
 */
export default class FullWorkspaceRemoteEvaluator
  implements WorkspaceRemoteEvaluator<FullWorkspaceEvaluateRequest, FullWorkspaceEvaluateResponse>
{
  constructor(private readonly client: RemoteEvaluateClient) {}

  async evaluate(request: FullWorkspaceEvaluateRequest): Promise<FullWorkspaceEvaluateResponse> {
    const response = await this.client.evaluateIfModified(request.toDto())
    return this.resolveResponse(request, response)
  }

  private async resolveResponse(
    request: FullWorkspaceEvaluateRequest,
    response: WorkspaceEvaluateResponseDto | null
  ): Promise<FullWorkspaceEvaluateResponse> {
    if (response === null) {
      // not-modified (HTTP 204)
      const base = ObjectUtil.requiredNotNullOrUndefined(request.base, () => "request.base")
      return new FullWorkspaceEvaluateResponse(base)
    }
    switch (response.status) {
      case "FULL":
        return this.resolveFull(request, response)
      case "DELTA":
        return this.resolveDelta(request, response)
      default:
        throw new Error(`Unsupported workspace evaluate status [${response.status}]`)
    }
  }

  private async resolveFull(
    request: FullWorkspaceEvaluateRequest,
    response: WorkspaceEvaluateResponseDto
  ): Promise<FullWorkspaceEvaluateResponse> {
    const full = ObjectUtil.requiredNotNullOrUndefined(response.full, () => "response.full")
    const context = WorkspaceEvaluationContext.of(request.context.key, full, full.metadata.evaluatedAt)
    return new FullWorkspaceEvaluateResponse(context)
  }

  private async resolveDelta(
    request: FullWorkspaceEvaluateRequest,
    response: WorkspaceEvaluateResponseDto
  ): Promise<FullWorkspaceEvaluateResponse> {
    const base = ObjectUtil.requiredNotNullOrUndefined(request.base, () => "request.base")
    const delta = ObjectUtil.requiredNotNullOrUndefined(response.delta, () => "response.delta")

    const merged = WorkspaceEvaluations.merge(base.dto, delta)
    const mergedHash = WorkspaceEvaluations.hash(merged.results)

    if (mergedHash !== delta.metadata.hash) {
      const evaluation = await this.client.evaluateIfModified(request.toForceFull().toDto())
      return this.resolveFull(
        request,
        ObjectUtil.requiredNotNullOrUndefined(evaluation, () => "response")
      )
    }

    const context = WorkspaceEvaluationContext.of(base.key, merged, base.fullEvaluatedAt)
    return new FullWorkspaceEvaluateResponse(context)
  }
}
